import React, { useContext, useEffect, useRef, useState } from "react"
import { animate, useMotionValue, useReducedMotion } from "framer-motion"
import { profileContext } from "./profile.provider"
import {
	getLessonsByCoaches,
	getLessonsBySchool,
	getLessonsInRange,
} from "../utils/firestore.utils"
import { managedCoachIDsIn, seenCoachIDsIn, seesAllLessonsIn } from "../utils/func.utils"
import { schoolContext } from "./school.provider"
import { DateTime } from "luxon"
import { isDesktopLike } from "../atoms/DesktopLikeView"
import { isSmartphone } from "../atoms/SmartphoneView"
import { getWeekBounds, LESSONS_VIEWS, sortByStart } from "../utils/week.utils"
import { heldLessons } from "../utils/cancellation.utils"
import { VIEW_SWITCH_TRANSITION } from "../week/viewMorph"
import { isArchivedSchool } from "../utils/school.utils"

const NO_LESSONS_ELSEWHERE = {}

export const lessonsContext = React.createContext()

// Da quale vista si parte, giorno o settimana. È una scelta di come si usa
// l'app su questo telefono o su questo computer, come il tema: sta qui e non
// sul profilo, e su un computer si può volere la settimana e sul telefono no.
const DEFAULT_VIEW_STORAGE_KEY = "incampo.lessonsView"

const readDefaultLessonsView = () => {
	try {
		const stored = window.localStorage.getItem(DEFAULT_VIEW_STORAGE_KEY)
		return LESSONS_VIEWS.includes(stored) ? stored : "day"
	} catch {
		return "day"
	}
}

// Le lezioni che il maestro ha negli altri circoli, viste in trasparenza
// accanto a quelle del circolo scelto: se accenderle e quali circoli
// lasciare fuori. Come la vista e il tema è una scelta di questo telefono o
// di questo computer: il documento del maestro lo scrive solo il server, e
// per una preferenza di come guardare le lezioni non vale una funzione.
const OTHER_SCHOOLS_STORAGE_KEY = "incampo.otherSchools"
const NO_OTHER_SCHOOLS = { enabled: false, hidden: [] }

const readOtherSchoolsPreference = () => {
	try {
		const stored = JSON.parse(window.localStorage.getItem(OTHER_SCHOOLS_STORAGE_KEY))
		return {
			enabled: stored?.enabled === true,
			hidden: Array.isArray(stored?.hidden) ? stored.hidden : [],
		}
	} catch {
		return NO_OTHER_SCHOOLS
	}
}

// Se il "+" chiede "Lezione o Gioco io?" (src/utils/match.utils.js). Acceso
// di partenza; chi non gioca lo spegne, e il "+" torna ad aprire subito la
// lezione. Come la vista e il tema, vale su questo telefono o questo computer.
const MATCHES_STORAGE_KEY = "incampo.matches"

const readMatchesEnabled = () => {
	try {
		return window.localStorage.getItem(MATCHES_STORAGE_KEY) !== "off"
	} catch {
		return true
	}
}

const LessonsProvider = ({ children }) => {
	const { coach, displayedCoachID } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)

	const [lessons, setLessons] = useState({})
	const [selectedDate, setSelectedDate] = useState(DateTime.now().toISO().split("T")[0])
	// Come per gli allievi: all'avvio non si sa ancora niente, e "niente
	// lezioni" è una risposta, non uno stato di partenza.
	const [isLoading, setLoadingState] = useState(true)
	const [refreshToken, setRefreshToken] = useState(0)

	const [defaultLessonsView, setDefaultLessonsViewState] = useState(readDefaultLessonsView)
	// La vista del momento: parte da quella scelta nel profilo, e l'interruttore
	// nella fascia in cima la cambia senza toccare la scelta.
	const [lessonsView, setLessonsViewState] = useState(readDefaultLessonsView)
	// Sul telefono il passaggio tra le due è un avanzamento da 0 (giorno) a 1
	// (settimana) che le dita possono tenere a metà (vedi week/viewMorph).
	// Cambiando vista da un bottone ci va da solo; col pizzico lo portano le
	// dita, e alla fine si sceglie la vista dove si è arrivati.
	const viewProgress = useMotionValue(lessonsView === "week" ? 1 : 0)
	const prefersLessMotion = useReducedMotion()

	const setLessonsView = view => {
		setLessonsViewState(view)
		const target = view === "week" ? 1 : 0
		if (!isSmartphone || prefersLessMotion) viewProgress.set(target)
		else animate(viewProgress, target, VIEW_SWITCH_TRANSITION)
	}

	const [otherSchools, setOtherSchoolsState] = useState(readOtherSchoolsPreference)

	const saveOtherSchools = next => {
		setOtherSchoolsState(next)
		try {
			window.localStorage.setItem(OTHER_SCHOOLS_STORAGE_KEY, JSON.stringify(next))
		} catch {
			// vale per questa sessione
		}
	}
	const setShowOtherSchools = enabled => saveOtherSchools({ ...otherSchools, enabled })
	const setOtherSchoolHidden = (schoolID, hidden) =>
		saveOtherSchools({
			...otherSchools,
			hidden: hidden
				? [...new Set([...otherSchools.hidden, schoolID])]
				: otherSchools.hidden.filter(id => id !== schoolID),
		})

	const [matchesEnabled, setMatchesEnabledState] = useState(readMatchesEnabled)
	const setMatchesEnabled = enabled => {
		setMatchesEnabledState(enabled)
		try {
			window.localStorage.setItem(MATCHES_STORAGE_KEY, enabled ? "on" : "off")
		} catch {
			// vale per questa sessione
		}
	}

	const setDefaultLessonsView = view => {
		if (!LESSONS_VIEWS.includes(view)) return
		setDefaultLessonsViewState(view)
		try {
			window.localStorage.setItem(DEFAULT_VIEW_STORAGE_KEY, view)
		} catch {
			// vale per questa sessione
		}
	}

	// Le lezioni della settimana mostrata, per giorno. Stanno fuori dalla
	// cache del giorno: lì sul computer chi gestisce altri maestri tiene un
	// secchio per maestro scritto dagli ascolti del giorno, e due ascolti che
	// riscrivono lo stesso secchio con contenuti diversi si darebbero il
	// cambio a ogni aggiornamento.
	const [weekLessons, setWeekLessons] = useState({ key: null, byDate: null })

	// La lezione da aprire quando dalla settimana si passa al giorno: la
	// legge l'elenco del giorno, la apre e la dimentica.
	const [lessonToOpen, setLessonToOpen] = useState(null)

	const changeSelectedDate = day => {
		setSelectedDate(DateTime.fromISO(selectedDate).plus({ days: day }).toISO().split("T")[0])
	}

	const mergeLessons = (date, coachID, newLessons) => {
		setLessons(prev => ({
			...prev,
			[selectedSchool]: {
				...(prev[selectedSchool] || {}),
				[date]: {
					...(prev[selectedSchool]?.[date] || {}),
					[coachID]: newLessons,
				},
			},
		}))
	}

	// rimuove una lezione dalla cache senza aspettare lo snapshot di Firestore,
	// così la card sparisce subito dopo l'eliminazione o lo spostamento
	const removeLessonFromCache = ({ schoolID, date, coachID, lessonID }) => {
		if (!schoolID || !date || !coachID) return

		setLessons(prev => {
			const dayLessons = prev[schoolID]?.[date]?.[coachID]
			if (!dayLessons) return prev

			return {
				...prev,
				[schoolID]: {
					...prev[schoolID],
					[date]: {
						...prev[schoolID][date],
						[coachID]: dayLessons.filter(lesson => lesson.lessonID !== lessonID),
					},
				},
			}
		})
	}

	// Gli ascolti del giorno aperti, per `circolo_giorno_maestri`. Resta
	// aperto solo quello del giorno che si guarda: un giorno lasciato in
	// ascolto costa letture anche quando non si vede, e tornando all'app dopo
	// una pausa lunga Firestore li rilegge tutti da capo.
	const listenersRef = useRef({})

	// chiude i listener di un giorno e ne fa riaprire di nuovi, così la cache
	// viene riletta da Firestore: serve dopo un'eliminazione o uno spostamento
	const invalidateDate = (date, schoolID = selectedSchool) => {
		if (!date || !schoolID) return

		const prefix = `${schoolID}_${date}_`
		Object.keys(listenersRef.current).forEach(key => {
			if (!key.startsWith(prefix)) return
			listenersRef.current[key]?.()
			delete listenersRef.current[key]
		})

		setRefreshToken(token => token + 1)
	}

	useEffect(() => {
		return () => {
			Object.values(listenersRef.current).forEach(u => u && u())
			listenersRef.current = {}
		}
	}, [])

	useEffect(() => {
		if (!coach || !selectedSchool || !selectedDate) {
			// Si esce dall'account: gli ascolti di chi c'era si chiudono con
			// lui. Restando in elenco, rientrando nella stessa pagina il giorno
			// risultava già ascoltato, non si riapriva, e restava vuoto.
			Object.values(listenersRef.current).forEach(unsubscribe => unsubscribe?.())
			listenersRef.current = {}
			setLessons({})
			setLoadingState(false)
			return
		}

		// sul computer si vede una colonna per maestro: di tutti quelli che si
		// guardano, non solo di chi si modifica
		const isSchoolManager = seesAllLessonsIn(coach, selectedSchool)
		const managedCoachesIDs = [
			...new Set([
				...managedCoachIDsIn(coach, selectedSchool),
				...seenCoachIDsIn(coach, selectedSchool),
			]),
		]

		const subscribeToDate = (date, coachIDs) => {
			const key = `${selectedSchool}_${date}_${coachIDs.join(",")}`
			if (listenersRef.current[key]) return
			setLoadingState(true)

			const handleSnapshot = async groupedLessons => {
				const lessonsForDay = groupedLessons[date] || []

				if (isDesktopLike && (isSchoolManager || managedCoachesIDs.length > 0)) {
					// bucket per coachID principale
					lessonsForDay.forEach(l => {
						mergeLessons(
							date,
							l.coachID,
							lessonsForDay.filter(x => x.coachID === l.coachID),
						)
					})

					// merge anche nei bucket degli assistenti
					// Ricostruisci i bucket degli assistenti da zero ad ogni snapshot
					const assistantBuckets = {}
					lessonsForDay.forEach(l => {
						if (l.assistantCoachIDs?.length) {
							l.assistantCoachIDs.forEach(assistantID => {
								if (!assistantBuckets[assistantID])
									assistantBuckets[assistantID] = []
								assistantBuckets[assistantID].push(l)
							})
						}
					})

					// Ora scrivi i bucket ordinati nello state (sovrascrittura, non append)
					Object.entries(assistantBuckets).forEach(([assistantID, assistantLessons]) => {
						assistantLessons.sort(
							(a, b) => a.lessonStart.toDate() - b.lessonStart.toDate(),
						)
						mergeLessons(date, assistantID, assistantLessons)
					})

				} else {
					mergeLessons(date, coachIDs[0], lessonsForDay)
				}

				setLoadingState(false)
			}

			let unsubscribe
			if (isDesktopLike && isSchoolManager) {
				unsubscribe = getLessonsBySchool(selectedSchool, date, handleSnapshot)
			} else if (isDesktopLike && managedCoachesIDs.length > 0) {
				unsubscribe = getLessonsByCoaches(
					[coach.coachID, ...managedCoachesIDs],
					selectedSchool,
					date,
					handleSnapshot,
				)
			} else if (coachIDs.length > 0) {
				// lezioni come titolare e come assistente: `getLessonsByCoaches`
				// apre già entrambe le query per ognuno dei coach, e le unisce.
				// Ascoltare a parte anche quelle da assistente voleva dire
				// leggerle due volte per ogni giorno mostrato.
				unsubscribe = getLessonsByCoaches(coachIDs, selectedSchool, date, handleSnapshot)
			}

			if (typeof unsubscribe === "function") {
				listenersRef.current[key] = unsubscribe
			} else {
				setLoadingState(false)
				console.warn(
					"subscribeToDate: getLessons... non ha restituito unsubscribe per",
					key,
				)
			}
		}

		// Sul telefono il giorno arriva già con la settimana (vedi `weekLessons`
		// qui sotto): un ascolto suo leggerebbe le stesse lezioni due volte.
		const wantedKey =
			!isSmartphone && displayedCoachID
				? `${selectedSchool}_${selectedDate}_${displayedCoachID}`
				: null
		Object.keys(listenersRef.current).forEach(key => {
			if (key === wantedKey) return
			listenersRef.current[key]?.()
			delete listenersRef.current[key]
		})
		if (wantedKey) subscribeToDate(selectedDate, [displayedCoachID])
		else setLoadingState(false)
	}, [coach, selectedSchool, selectedDate, isDesktopLike, displayedCoachID, refreshToken])

	const { start: weekStart, end: weekEnd } = getWeekBounds(selectedDate)
	// Sul telefono giorno e settimana sono la stessa pagina (vedi
	// week/PhoneLessons): anche il giorno si legge dalla settimana, che resta
	// pronta mentre le dita passano dall'uno all'altra. Sono due ascolti in
	// più, una settimana alla volta.
	const weekKey =
		(lessonsView === "week" || isSmartphone) && displayedCoachID && selectedSchool
			? `${selectedSchool}_${displayedCoachID}_${weekStart}`
			: null

	// Una query sola per la settimana — da titolare e da assistente — invece
	// di sette ascolti, uno per giorno. Sul computer si apre solo quando la
	// settimana si guarda davvero.
	useEffect(() => {
		if (!weekKey) return

		setWeekLessons({ key: weekKey, byDate: null })
		const unsubscribe = getLessonsInRange(
			displayedCoachID,
			selectedSchool,
			DateTime.fromISO(weekStart).toJSDate(),
			DateTime.fromISO(weekEnd).toJSDate(),
			byDate => setWeekLessons({ key: weekKey, byDate }),
		)
		return () => unsubscribe()
	}, [weekKey, displayedCoachID, selectedSchool, weekStart, weekEnd])

	// Gli altri circoli in cui si insegna, quelli che la preferenza lascia
	// vedere. Solo guardando le proprie lezioni: le ore altrove di un collega
	// non sono affari di chi lo gestisce qui. Gli archiviati no: lì non si
	// lavora più, e le ore di allora sono storia, non un ingombro di oggi.
	const otherSchoolIDs = (coach?.teachesIn ?? []).filter(
		id => id !== selectedSchool && !isArchivedSchool(coach, id),
	)
	const shownOtherSchoolIDs =
		otherSchools.enabled && coach && displayedCoachID === coach.coachID
			? otherSchoolIDs.filter(id => !otherSchools.hidden.includes(id))
			: []
	const elsewhereKey =
		shownOtherSchoolIDs.length > 0
			? `${coach.coachID}|${weekStart}|${shownOtherSchoolIDs.join(",")}`
			: null

	// Le lezioni negli altri circoli, per la settimana che contiene il giorno
	// scelto: serve al giorno e alla settimana, e cambiando giorno dentro la
	// stessa settimana non si riascolta niente. Una coppia di ascolti per
	// circolo, perché le regole vogliono ogni query chiusa in un circolo.
	const [elsewhereLessons, setElsewhereLessons] = useState({ key: null, byDate: {} })

	useEffect(() => {
		if (!elsewhereKey) return

		const bySchool = {}
		const emit = () => {
			const byDate = {}
			Object.values(bySchool).forEach(schoolByDate =>
				Object.entries(schoolByDate).forEach(([date, dayLessons]) => {
					byDate[date] = sortByStart([...(byDate[date] ?? []), ...heldLessons(dayLessons)])
				}),
			)
			setElsewhereLessons({ key: elsewhereKey, byDate })
		}

		// la chiave dice già tutto quello da cui dipende l'ascolto: maestro,
		// settimana e circoli
		const unsubscribers = shownOtherSchoolIDs.map(schoolID =>
			getLessonsInRange(
				coach.coachID,
				schoolID,
				DateTime.fromISO(weekStart).toJSDate(),
				DateTime.fromISO(weekEnd).toJSDate(),
				byDate => {
					bySchool[schoolID] = byDate
					emit()
				},
			),
		)
		return () => unsubscribers.forEach(unsubscribe => unsubscribe())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elsewhereKey])

	return (
		<lessonsContext.Provider
			value={{
				lessons,
				selectedDate,
				isLoading,
				lessonsView,
				setLessonsView,
				viewProgress,
				// le altre scuole del maestro, e quali si vedono in trasparenza
				otherSchoolIDs,
				showOtherSchools: otherSchools.enabled,
				hiddenOtherSchools: otherSchools.hidden,
				setShowOtherSchools,
				setOtherSchoolHidden,
				// i circoli di cui si vedono le lezioni, e la settimana caricata
				shownOtherSchoolIDs,
				elsewhereWeekStart: elsewhereKey ? weekStart : null,
				// lezioni negli altri circoli, per data; vuoto se non se ne vedono
				elsewhereLessons:
					elsewhereKey && elsewhereLessons.key === elsewhereKey
						? elsewhereLessons.byDate
						: NO_LESSONS_ELSEWHERE,
				defaultLessonsView,
				setDefaultLessonsView,
				// il "+" chiede "Lezione o Gioco io?"
				matchesEnabled,
				setMatchesEnabled,
				// finché la query della settimana giusta non ha risposto non si sa
				// ancora niente: null, non una settimana vuota
				weekLessons: weekLessons.key === weekKey ? weekLessons.byDate : null,
				lessonToOpen,
				setLessonToOpen,
				setLessons,
				setSelectedDate,
				changeSelectedDate,
				mergeLessons,
				removeLessonFromCache,
				invalidateDate,
			}}>
			{children}
		</lessonsContext.Provider>
	)
}

export default LessonsProvider
